import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, Input } from "../../components/ui";
import { useLocale, useT } from "../../i18n";
import { apiJson } from "../../lib/api";
import type { AccountInfo } from "../../../shared/api-types";

function formatBytes(bytes: number, locale: string): string {
  const fmt = new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-US", {
    maximumFractionDigits: 1,
  });
  const GB = 1024 ** 3;
  const MB = 1024 ** 2;
  if (bytes >= GB) return `${fmt.format(bytes / GB)} GB`;
  if (bytes >= MB) return `${fmt.format(bytes / MB)} MB`;
  return `${fmt.format(bytes / 1024)} KB`;
}

/** Aktiver Account: Name (Account-Admin) + Speicherverbrauch — Billing & Tarife docken später hier an. */
export function CompanySettings() {
  const t = useT();
  const [locale] = useLocale();
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    apiJson<AccountInfo>("/api/account")
      .then(setInfo)
      .catch(() => setLoadError(true));
  }, []);

  async function submitName(e: FormEvent) {
    e.preventDefault();
    if (!info || nameDraft === null || nameDraft.trim() === "") return;
    setBusy(true);
    setMsg(null);
    try {
      await apiJson("/api/account", { method: "PATCH", json: { name: nameDraft.trim() } });
      setInfo({ ...info, name: nameDraft.trim() });
      setMsg({ ok: true, text: t.company.nameChanged });
    } catch {
      setMsg({ ok: false, text: t.common.error });
    }
    setBusy(false);
  }

  if (loadError) {
    return <p className="text-sm text-red-600 dark:text-red-400">{t.company.loadError}</p>;
  }
  if (!info) {
    return <p className="text-sm text-zinc-400">{t.common.loading}</p>;
  }
  const isOwner = info.role === "owner";

  return (
    <div className="max-w-xl space-y-5">
      <Card>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
          {t.company.nameLabel}
        </h4>
        {isOwner ? (
          <form onSubmit={(e) => void submitName(e)} className="flex gap-2">
            <Input
              className="min-w-0 flex-1"
              required
              value={nameDraft ?? info.name}
              onChange={(e) => setNameDraft(e.target.value)}
            />
            <Button type="submit" variant="ghost" disabled={busy}>
              {t.account.change}
            </Button>
          </form>
        ) : (
          <>
            <p className="text-sm font-medium">{info.name}</p>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t.company.memberHint}</p>
          </>
        )}
        {msg && (
          <p
            className={`mt-2 text-xs ${
              msg.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
            }`}
          >
            {msg.text}
          </p>
        )}
      </Card>

      <Card>
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
          {t.company.storageSection}
        </h4>
        <p className="text-2xl font-semibold tracking-tight">
          {formatBytes(info.storage.bytes, locale)}
        </p>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          {t.company.tourCount(info.storage.tourCount)} ·{" "}
          {t.dashboard.sceneCount(info.storage.sceneCount)}
        </p>
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">{t.company.storageHint}</p>
        <p className="mt-4 border-t border-zinc-100 pt-3 text-xs text-zinc-400 dark:border-zinc-800 dark:text-zinc-500">
          {t.company.billingHint}
        </p>
      </Card>
    </div>
  );
}
